import React, { useState, useRef } from 'react';
import './contact_request.css';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { apiEndpoints, quoteTypes, uploadConfig } from '../../constants/constants';

const ContactRequest = () => {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    quoteType: quoteTypes.length > 0 ? quoteTypes[0] : '',
    message: '',
    uploadId: ''
  });
  
  // File upload related states
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const toggleModal = () => {
    setIsOpen(!isOpen);
    // Reset error state when toggling modal
    setError(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validate number of files
    if (selectedFiles.length > uploadConfig.maxFiles) {
      setUploadError(`You can only upload up to ${uploadConfig.maxFiles} files at once.`);
      return;
    }
    
    // Validate file sizes
    const maxSizeInBytes = uploadConfig.maxSizeInMB * 1024 * 1024;
    const oversizedFiles = selectedFiles.filter(file => file.size > maxSizeInBytes);
    
    if (oversizedFiles.length > 0) {
      setUploadError(`Some files exceed the maximum size of ${uploadConfig.maxSizeInMB} MB.`);
      return;
    }
    
    // Clear any previous errors
    setUploadError(null);
    setFiles(selectedFiles);
    
    // Start upload immediately when files are selected
    if (selectedFiles.length > 0) {
      handleUpload(selectedFiles);
    }
  };
  
  // Upload files using pre-signed URLs
  const handleUpload = async (filesToUpload) => {
    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      // Get the file names
      const fileNames = filesToUpload.map(file => file.name);
      
      // Request pre-signed URLs from Lambda
      console.log('Requesting pre-signed URLs from:', apiEndpoints.fileUpload);
      
      // Using a more verbose axios approach for better CORS handling
      const response = await axios({
        method: 'post',
        url: apiEndpoints.fileUpload,
        data: { fileNames: fileNames },
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Pre-signed URL response:', response);
      
      if (!response.data || !response.data.uploadId || !response.data.uploadUrls) {
        throw new Error('Invalid response from upload server');
      }
      
      const { uploadId, uploadUrls } = response.data;
      
      // Store the upload ID in form data
      setFormData(prevData => ({
        ...prevData,
        uploadId: uploadId
      }));
      
      // Upload each file using its pre-signed URL
      const totalFiles = filesToUpload.length;
      let completedFiles = 0;
      
      const uploadPromises = filesToUpload.map(async (file) => {
        const presignedUrl = uploadUrls[file.name];
        
        if (!presignedUrl) {
          throw new Error(`No pre-signed URL found for ${file.name}`);
        }
        
        // Upload the file to S3 using the pre-signed URL
        console.log(`Starting upload for ${file.name} to URL: ${presignedUrl.substring(0, 100)}...`);
        
        await axios({
          method: 'put',
          url: presignedUrl,
          data: file,
          headers: {
            'Content-Type': 'application/octet-stream'
          },
          onUploadProgress: (progressEvent) => {
            // This progress is for individual file, we'll update the overall progress later
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`${file.name} upload progress: ${percentCompleted}%`);
          }
        });
        
        // Update overall progress
        completedFiles++;
        const overallProgress = Math.round((completedFiles * 100) / totalFiles);
        setUploadProgress(overallProgress);
      });
      
      // Wait for all uploads to complete
      await Promise.all(uploadPromises);
      
      console.log('All files uploaded successfully!');
      setUploadProgress(100);
    } catch (error) {
      console.error('Error uploading files:', error);
      setUploadError('Failed to upload files. Please try again.');
      // Reset upload ID if upload failed
      setFormData(prevData => ({
        ...prevData,
        uploadId: ''
      }));
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Don't allow submission if uploads are in progress
    if (isUploading) {
      setError('Please wait for file uploads to complete before submitting.');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Send form data to the lambda endpoint, including upload ID if files were uploaded
      const response = await axios.post(
        apiEndpoints.quotationRequest,
        formData
      );
      
      if (response.status === 200) {
        // Reset form and close modal on success
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          quoteType: quoteTypes.length > 0 ? quoteTypes[0] : '',
          message: '',
          uploadId: ''
        });
        setFiles([]);
        setUploadProgress(0);
        setIsOpen(false);
        // Show confirmation message
        alert('Thank you! Your request has been submitted.');
      } else {
        setError('There was an issue submitting your request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('There was an issue connecting to our server. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`contact-request-container ${theme}`}>
      <button className="request-quote-btn" onClick={toggleModal}>
        Request a Quotation
      </button>

      {isOpen && (
        <div className="modal-overlay">
          <div className="quote-modal" role="dialog" aria-labelledby="dialog-title">
            <div className="modal-header">
              <h3 id="dialog-title">Request a Quotation</h3>
              <button className="close-modal" onClick={toggleModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit} className="quote-form">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="company">Company *</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="quoteType">Type of Quote *</label>
                <select
                  id="quoteType"
                  name="quoteType"
                  value={formData.quoteType}
                  onChange={handleChange}
                  required
                >
                  {quoteTypes.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group form-group-message">
                <label htmlFor="message">Project Details *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="8"
                  required
                  placeholder="Please describe your project needs and requirements in detail..."
                ></textarea>
              </div>
              
              <div className="form-group file-upload-container">
                <label htmlFor="file-upload">
                  Upload Files <span className="optional-label">(Optional)</span>
                </label>
                <div className="file-upload-box">
                  <input
                    type="file"
                    id="file-upload"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    multiple
                    disabled={isUploading}
                    className="file-input"
                  />
                  <div className="file-upload-content">
                    <div className="file-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="17 8 12 3 7 8"></polyline>
                        <line x1="12" y1="3" x2="12" y2="15"></line>
                      </svg>
                    </div>
                    <div className="file-text">
                      {files.length > 0 ? (
                        <span>{files.length} file(s) selected</span>
                      ) : (
                        <span>Drag & drop files here or click to browse</span>
                      )}
                      <small>Max {uploadConfig.maxFiles} files, up to {uploadConfig.maxSizeInMB} MB each</small>
                    </div>
                  </div>
                </div>
                
                {/* Upload progress and status */}
                {files.length > 0 && (
                  <div className="upload-status">
                    {isUploading ? (
                      <div className="upload-progress">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <span className="progress-text">Uploading... {uploadProgress}%</span>
                      </div>
                    ) : formData.uploadId ? (
                      <div className="upload-complete">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        <span>Files uploaded successfully</span>
                      </div>
                    ) : null}
                    
                    <div className="file-list">
                      {files.map((file, index) => (
                        <div key={index} className="file-item">
                          <span className="file-name">{file.name}</span>
                          <span className="file-size">({(file.size / (1024 * 1024)).toFixed(2)} MB)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {uploadError && (
                <div className="error-message">
                  {uploadError}
                </div>
              )}
              
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}
              <div className="form-actions">
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={isSubmitting || isUploading}
                >
                  {isSubmitting ? 'Submitting...' : isUploading ? 'Uploading Files...' : 'Submit Request'}
                </button>
                <button 
                  type="button" 
                  className="cancel-btn" 
                  onClick={toggleModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactRequest;